import { type TrainerStats } from "@fitness-app/data-models/entities/TrainerStats";

import { type GhostClientWithInfo } from "./hooks/fetchTrainerGhostClients";

export function exportTrainerChartDataToCsv(
  stats: TrainerStats[],
  ghostClients: GhostClientWithInfo[],
  trainerName: string,
  selectedMonth: string,
  renewalsByDate: Record<string, string[]> = {},
) {
  // Sort stats by date chronologically
  const sortedStats = [...stats].sort((a, b) => a.date.localeCompare(b.date));

  // Create a map of stat ID -> stat for O(1) lookup
  const statsById = new Map(sortedStats.map((s) => [s.id, s]));

  // Create a map of date -> ghost client emails
  const ghostClientsByDate = new Map<string, string[]>();

  // Group ghost clients by date
  ghostClients.forEach((client) => {
    const stat = statsById.get(client.dayId);
    if (stat && client.trainee?.email) {
      const date = stat.date;
      if (!ghostClientsByDate.has(date)) {
        ghostClientsByDate.set(date, []);
      }
      ghostClientsByDate.get(date)!.push(client.trainee.email);
    }
  });

  // Headers
  const headers = [
    "Data",
    "Aktywni",
    "Zamrożeni",
    "Duchy",
    "Odnowienia",
    "Archiwizacja",
    "Zmiana trenera",
    "Duchy - Email",
  ];

  const dataRows: string[][] = [];

  sortedStats.forEach((stat, index) => {
    const ghostEmails = ghostClientsByDate.get(stat.date) || [];

    dataRows[index] = [
      stat.date,
      String(Math.max(0, stat.totalClients - stat.totalFrozen - stat.totalGhost)),
      String(stat.totalFrozen),
      String(stat.totalGhost),
      String(renewalsByDate[stat.date]?.length ?? 0),
      String(stat.metadata.archivedTrainees ?? 0),
      String(stat.metadata.assignedToDifferentTrainer ?? 0),
      ghostEmails.join("; "),
    ];
  });

  const rows = [headers, ...dataRows];
  const csvContent = rows
    .map((row) =>
      row
        .map((cell) => {
          // Escape quotes and wrap in quotes if contains comma, semicolon, or quote
          const cellStr = String(cell);
          if (cellStr.includes(",") || cellStr.includes(";") || cellStr.includes('"') || cellStr.includes("\n")) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(";"),
    )
    .join("\n");

  // Add UTF-8 BOM for proper Excel recognition
  const BOM = "\uFEFF";
  const csvData = BOM + csvContent;

  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csvData], { type: "text/csv;charset=utf-8;" }));
  a.setAttribute("download", `trainer-${trainerName}-${selectedMonth}-stats.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
